/**
 * Job/department-driven Step6 behaviors that are NOT derivable from a
 * role's flat `permissions[]` list — they key off `roleName` /
 * `capsSessionBean.getRoleName()` directly (matrix §2b), e.g.:
 *   - MAM / DOS / IMAGING TEAM  -> reduced Internal-section menu
 *   - "OCE" department          -> Exit hidden unless VLA-lock owner; Actions always read-only
 *   - DPSC / DEALER COMPLIANCE / ITSUPPORT -> click-to-call icons
 *   - DSC FUNDING SUPERVISOR / DSC FUNDING MANAGER -> reassignment approval at dealer rating 7/8
 *   - Funding Compliance department (auditUser) -> Stipulations audit-compliance edit rights
 *
 * TODO(role-mapping): the current 23-role Workbench role catalog does not
 * line up 1:1 with the legacy department strings above — there is no bare
 * "DPSC" or "OCE" role, and "DEALER COMPLIANCE" is split into
 * "DEALER COMPLIANCE AUDIT ANALYST/SUPERVISOR/MANAGER". Confirm the correct
 * new-role -> legacy-department mapping with product/business (see matrix
 * §7 open question #2) before wiring any of the behaviors above into the
 * Workbench. Department mappings remain unimplemented until confirmed;
 * the explicit ITSUPPORT role needs no department mapping.
 */

/** Roles that should see Internal's reduced 1-item menu instead of the full nav. */
export const REDUCED_INTERNAL_MENU_ROLES: ReadonlySet<string> = new Set();

/** Roles whose Actions panel is always read-only, department-based Exit rules, etc. */
export const OCE_DEPARTMENT_ROLES: ReadonlySet<string> = new Set();

/** Roles that see click-to-call phone icons across Dealer Info / Income / Residence / Vehicle. */
export const CLICK_TO_CALL_ROLES: ReadonlySet<string> = new Set(["ITSUPPORT"]);

/**
 * Whether the user sees click-to-call icons. CAPS2 also grants this to the DPSC and DEALER COMPLIANCE
 * departments; the user payload carries no department yet, so this is the one place to add that check.
 * @param {string} [userRole] - The signed-in user's role name.
 * @returns {boolean} True when the role is allowed to place calls.
 */
export function canClickToCall(userRole?: string): boolean {
  return CLICK_TO_CALL_ROLES.has(userRole?.trim().toUpperCase() ?? "");
}

/** Roles allowed to submit a reassignment when dealer rating is 7 or 8. */
export const REASSIGNMENT_APPROVER_ROLES: ReadonlySet<string> = new Set();

/** Roles allowed to edit Stipulations audit-compliance fields while in audit mode. */
export const AUDIT_COMPLIANCE_EDITOR_ROLES: ReadonlySet<string> = new Set();
